// frontend/src/components/shared/inlineMarkup.js
//
// The site's tiny inline-markup parser: **bold** and [label](href) links.
// No markdown library — this is the whole dialect.
//
// Used by BOTH the blog (components/blog/ArticleBody.js, over blogData.js) and
// the pricing surfaces (components/pricing/planFeatures.js, over the plan copy
// served by /pricing). It lives here rather than in ArticleBody because the
// homepage imports the pricing card EAGERLY (App.js -> Homepage -> PricingSection),
// so importing it from the blog would drag ArticleBody's block renderers
// (Para/BlogTable/ListBlock/ArticleBody) into the eager bundle, where they can
// never execute. ArticleBody re-exports renderInline for its existing callers.
//
// ── SECURITY: hrefs are treated as UNTRUSTED ──────────────────────────────────
// Plan copy arrives over the public /pricing API, so this parser turns remote
// strings into anchors. Only an allow-list is honoured (see classifyHref);
// anything else renders as plain text and the link is dropped. Do NOT relax this
// to "starts with /" — that admits protocol-relative "//evil.com", which is
// off-origin despite the leading slash. React 19's href sanitiser does not cover
// that case, so this module is the only thing standing between a malicious
// string and an off-origin navigation from /payment.

import React from "react";
import { Link as MuiLink } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const ACCENT = "#00A896"; // teal accent (matches homepage eyebrow/CTAs)

export const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;

export const LINK_SX = {
  color: ACCENT,
  fontWeight: 600,
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
};

// Schemes an author may link to. Everything else (javascript:, data:, vbscript:,
// blob:, file:, ...) is dropped rather than rendered.
const ALLOWED_SCHEMES = new Set(["https:", "http:", "mailto:", "tel:"]);

/**
 * The scheme a BROWSER would resolve for this href, or null if it has none.
 *
 * Browsers ignore ASCII whitespace and C0 controls while parsing a scheme, so
 * "java\nscript:x" navigates as javascript:. The scheme test must therefore see
 * the same string the browser will — comparing against the raw text would let
 * "java\tscript:alert(1)" walk straight past the allow-list.
 *
 * @param {string} href
 * @returns {string|null} e.g. "https:", or null for a scheme-less reference.
 */
function schemeOf(href) {
  // The control chars in the class below are the whole point: browsers ignore
  // them while parsing a scheme, so the allow-list must strip the same set
  // before testing, or a "java<TAB>script:" href walks straight past it.
  // eslint-disable-next-line no-control-regex
  const normalized = href.replace(/[\u0000-\u0020\u007F]+/g, "").toLowerCase();
  const match = /^([a-z][a-z0-9+.-]*):/.exec(normalized);
  return match ? `${match[1]}:` : null;
}

/**
 * Decide how (or whether) an href may be rendered.
 *
 * @param {string} rawHref Href exactly as it appeared inside `(...)`.
 * @returns {{kind: "internal"|"external"|"rejected", href?: string}}
 */
export function classifyHref(rawHref) {
  const href = String(rawHref).trim();

  const scheme = schemeOf(href);
  if (scheme) {
    return ALLOWED_SCHEMES.has(scheme) ? { kind: "external", href } : { kind: "rejected" };
  }

  // Scheme-less: only a site-root-relative path is internal. "//host" is
  // protocol-relative and "/\host" is its backslash variant — browsers resolve
  // BOTH off-origin even though they start with "/", so neither may reach
  // RouterLink.
  if (href.startsWith("/") && !href.startsWith("//") && !href.startsWith("/\\")) {
    return { kind: "internal", href };
  }

  // Anything else (relative "foo/bar", bare "evil.com", empty) is not a shape
  // this dialect supports; fail closed.
  return { kind: "rejected" };
}

/** Split a plain string on **bold** markers into <strong> / text fragments. */
export function renderBold(text, baseKey) {
  return text
    .split("**")
    .map((seg, i) =>
      i % 2 === 1 ? (
        <strong key={`${baseKey}-b${i}`}>{seg}</strong>
      ) : (
        <React.Fragment key={`${baseKey}-t${i}`}>{seg}</React.Fragment>
      )
    );
}

/**
 * Parse a line for [label](href) links and **bold**, returning React nodes.
 *
 * A link whose href fails the allow-list degrades to its plain label — the words
 * still read correctly, only the (unsafe) link is dropped. In development the
 * drop is announced, because a silently un-linked word is easy to ship.
 *
 * A link LABEL is parsed for **bold** on every path — accepted, plain-mode and
 * dropped alike. The dropped path always did; the accepted paths used to emit the
 * label raw, so `[**x**](/p)` rendered literal asterisks to the reader depending
 * only on whether the href passed the allow-list. Uniform now, which is what lets
 * plan copy emphasise a phrase that happens to be a link label.
 *
 * `linkMode: "plain"` degrades EVERY link the same way, allow-listed or not, and
 * says nothing about it. That is not a security setting — it is for surfaces
 * where a navigation is the wrong affordance no matter where it points. The
 * checkout (/payment) is the one we have: its plan bullets carry the same copy
 * as the marketing cards, but a live link mid-checkout walks an authenticated
 * user off the page they were about to pay on. Wording stays identical across
 * both surfaces because the copy stays identical — only the anchor is withheld.
 * It is applied PER LIST, not per surface: the checkout passes it for the
 * Professional card (a marketing landing page) and not for Solo, whose
 * supporter-network bullet is a live link by owner decision.
 *
 * @param {string} text   Line of the inline dialect.
 * @param {object} [linkSx] Overrides merged over LINK_SX (e.g. white CTA links).
 * @param {{linkMode?: "link"|"plain"}} [options]
 * @returns {Array<React.ReactNode>}
 */
export function renderInline(text, linkSx, options) {
  const plainOnly = options?.linkMode === "plain";
  const sx = linkSx ? { ...LINK_SX, ...linkSx } : LINK_SX;
  const nodes = [];
  let lastIndex = 0;
  let idx = 0;
  let match;
  LINK_RE.lastIndex = 0;
  while ((match = LINK_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(...renderBold(text.slice(lastIndex, match.index), `s${idx}`));
    }
    const label = match[1];
    const { kind, href } = classifyHref(match[2]);

    if (plainOnly) {
      // A deliberate, per-surface choice - NOT a rejection, so it is silent.
      nodes.push(...renderBold(label, `p${idx}`));
    } else if (kind === "internal") {
      nodes.push(
        <MuiLink key={`l${idx}`} component={RouterLink} to={href} sx={sx}>
          {renderBold(label, `li${idx}`)}
        </MuiLink>
      );
    } else if (kind === "external") {
      nodes.push(
        <MuiLink
          key={`l${idx}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={sx}
        >
          {renderBold(label, `le${idx}`)}
        </MuiLink>
      );
    } else {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(
          `[inlineMarkup] dropped a link with a disallowed href: ${JSON.stringify(
            match[2]
          )}. Allowed: a site-root path ("/x", not "//x") or ${[...ALLOWED_SCHEMES].join(
            " / "
          )}.`
        );
      }
      nodes.push(...renderBold(label, `x${idx}`));
    }

    lastIndex = match.index + match[0].length;
    idx++;
  }
  if (lastIndex < text.length) {
    nodes.push(...renderBold(text.slice(lastIndex), `s${idx}`));
  }
  return nodes;
}
